/**
 * Animations utility using Intersection Observer API
 * This utility adds animation classes to elements when they enter the viewport
 */

// Initialize animations for elements with data-animate attribute
export function initAnimations(): void {
  // Only run in browser environment
  if (typeof window === "undefined" || typeof document === "undefined") return;

  // Animation options
  const options = {
    threshold: 0.1, // Trigger when 10% of the element is visible
    rootMargin: "0px 0px -50px 0px", // Trigger slightly before the element is fully in view
  };

  // Create observer
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        // Get animation type from data attribute
        const target = entry.target as HTMLElement;
        const animationType = target.dataset.animate || "fade-up";

        // Add the animation class
        target.classList.add("animate");
        target.classList.add(animationType);

        // Stop observing after animation is triggered
        observer.unobserve(target);
      }
    });
  }, options);

  // Observe all elements with data-animate attribute
  document.querySelectorAll("[data-animate]").forEach((element) => {
    observer.observe(element);
  });
}

// Initialize animations when document is loaded
export function setupAnimations(): void {
  if (typeof window !== "undefined") {
    // If document is already loaded
    if (document.readyState === "complete") {
      initAnimations();
    } else {
      // Otherwise, wait for document to load
      window.addEventListener("load", initAnimations);
    }

    // Also initialize on route changes (for SPAs)
    window.addEventListener("popstate", () => {
      setTimeout(initAnimations, 200); // Small delay to ensure DOM is updated
    });
  }
}
